import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Header,
  Jobs,
  Applications,
  Activity,
  Details,
  Documents,
  Notes,
} from '@/modules/workers/components';
import Breadcrumbs from '@/components/Breadcrumbs';
import Tabs from '@/components/Tabs';
import { Spinner } from '@/components/shared/Spinner';
import useFeatureView from '@/hooks/useFeatureView';
import { useWorkerDetailData } from '@/modules/workers/hooks/useWorkerDetailData';
import {
  getWorkerDetailTabs,
  getWorkerDetailView,
  type WorkerDetailTabKey,
} from '@/modules/workers/views/workerDetail.view';

const WorkerPage = () => {
  const { id } = useParams<{ id: string }>();
  const view = useFeatureView(getWorkerDetailView);
  const { worker, isLoading, error, refetchWorker } = useWorkerDetailData(
    view,
    id,
  );
  const workerTabs = getWorkerDetailTabs(view.tabs);
  const [selectedTab, setSelectedTab] = useState<WorkerDetailTabKey>('details');

  const handleLogoUpdate = async () => {
    if (view.actions.showLogoUpdate) {
      await refetchWorker();
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="animate-pulse">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !worker) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-600">{error || 'Worker not found'}</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Breadcrumbs
        items={[
          { label: 'Workers', to: '/workers' },
          {
            label: worker ? `${worker.firstName} ${worker.lastName}` : 'Worker',
          },
        ]}
        className="mb-4"
      />
      <Header
        id={worker.id}
        name={`${worker.firstName} ${worker.lastName}`}
        logoUrl={worker.profilePictureUrl ?? undefined}
        status={worker.status}
        onLogoUpdate={handleLogoUpdate}
      />
      <Tabs
        tabs={workerTabs}
        selectedTabKey={selectedTab}
        onTabChange={(key) => setSelectedTab(key as WorkerDetailTabKey)}
      >
        {selectedTab === 'details' && (
          <Details
            worker={worker}
            onViewNotes={() => setSelectedTab('notes')}
          />
        )}
        {selectedTab === 'documents' && <Documents worker={worker} />}
        {selectedTab === 'jobs' && <Jobs worker={worker} />}
        {selectedTab === 'notes' && <Notes worker={worker} />}
        {selectedTab === 'activity' && <Activity workerId={worker.id} />}
        {selectedTab === 'applications' && <Applications worker={worker} />}
      </Tabs>
    </div>
  );
};

export default WorkerPage;
